import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X, Inbox, Search } from 'lucide-react';

export const LOGO_URL = '/erp-logo.png';

export const fmtInr = (n) => {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  if (abs >= 1e7) return `₹ ${(v / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `₹ ${(v / 1e5).toFixed(1)} L`;
  return `₹ ${Math.round(v).toLocaleString('en-IN')}`;
};
export const fmtNum = (n) => Number(n || 0).toLocaleString('en-IN');
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
export const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} mins ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hours ago`;
  return `${Math.floor(s / 86400)} days ago`;
};
export const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('') || 'U';

export const Card = ({ children, className = '', hover = false, ...p }) => (
  <div className={`erp-card ${hover ? 'erp-card-hover' : ''} ${className}`} {...p}>{children}</div>
);

export const CardHeader = ({ title, subtitle, action, icon: Icon, className = '' }) => (
  <div className={`flex items-start justify-between gap-3 px-5 pt-5 pb-3 ${className}`}>
    <div className="flex items-center gap-2.5 min-w-0">
      {Icon && <span className="h-8 w-8 rounded-lg grid place-items-center" style={{ background: 'var(--erp-blue-soft)', color: 'var(--erp-blue)' }}><Icon size={16} /></span>}
      <div className="min-w-0">
        <h3 className="text-[15px] font-bold leading-tight truncate" style={{ color: 'var(--erp-ink)' }}>{title}</h3>
        {subtitle && <p className="text-xs mt-0.5" style={{ color: 'var(--erp-muted)' }}>{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

export const Button = ({ variant = 'primary', size = 'md', icon: Icon, children, className = '', ...p }) => (
  <button type="button" {...p} className={`erp-btn erp-btn-${variant} ${size === 'sm' ? 'erp-btn-sm' : ''} ${!children ? 'erp-btn-icon' : ''} ${className}`}>
    {Icon && <Icon size={size === 'sm' ? 14 : 16} />}{children}
  </button>
);

export const Label = ({ children, required }) => (
  <label className="block text-[12.5px] font-semibold mb-1.5" style={{ color: '#3b4664' }}>
    {children}{required && <span className="text-red-500 ml-0.5">*</span>}
  </label>
);

export const Input = ({ label, hint, required, className = '', ...p }) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <input required={required} {...p} className="erp-input" />
    {hint && <p className="text-[11.5px] mt-1" style={{ color: 'var(--erp-muted)' }}>{hint}</p>}
  </div>
);

export const Textarea = ({ label, className = '', ...p }) => (
  <div className={className}>{label && <Label>{label}</Label>}<textarea rows={3} {...p} className="erp-input" /></div>
);

const CHEVRON = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7590' stroke-width='2'><path d='m6 9 6 6 6-6'/></svg>\") no-repeat right 12px center";

export const Select = ({ label, required, children, className = '', ...p }) => (
  <div className={className}>
    {label && <Label required={required}>{label}</Label>}
    <select required={required} {...p} className="erp-input appearance-none pr-9" style={{ background: `${CHEVRON}, #fff` }}>
      {children}
    </select>
  </div>
);

export const SearchBox = ({ value, onChange, placeholder = 'Search…', className = '', ...p }) => (
  <div className={`relative ${className}`}>
    <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--erp-muted)' }} />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} {...p} className="erp-input erp-input-icon" />
  </div>
);

const TONES = {
  navy: { bg: 'var(--erp-navy)', fg: '#fff' }, blue: { bg: 'var(--erp-blue-soft)', fg: 'var(--erp-blue)' },
  green: { bg: '#e6f7ee', fg: '#127a45' }, red: { bg: '#fdecec', fg: '#c0262d' },
  amber: { bg: '#fff4e0', fg: '#a05e03' }, gray: { bg: '#eef1f7', fg: '#4b5674' },
};
export const Badge = ({ tone = 'gray', children, className = '', ...p }) => (
  <span {...p} className={`inline-flex items-center gap-1 px-2.5 h-6 rounded-full text-[11.5px] font-semibold ${className}`} style={{ background: TONES[tone].bg, color: TONES[tone].fg }}>{children}</span>
);

export const Segmented = ({ options, value, onChange, testid = 'segmented' }) => (
  <div className="inline-flex p-1 rounded-xl bg-white border" style={{ borderColor: 'var(--erp-line)' }} data-testid={testid}>
    {options.map((o) => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)} data-testid={`${testid}-${o.value}`}
        className="px-4 h-9 rounded-lg text-sm font-semibold transition-colors"
        style={value === o.value ? { background: 'var(--erp-navy)', color: '#fff' } : { color: 'var(--erp-muted)' }}>
        {o.label}
      </button>
    ))}
  </div>
);

export const PageHeader = ({ title, subtitle, actions, crumb }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6 erp-rise">
    <div>
      {crumb && <p className="text-[11.5px] font-semibold tracking-widest uppercase mb-1" style={{ color: 'var(--erp-blue)' }}>{crumb}</p>}
      <h1 className="text-2xl font-extrabold tracking-tight" style={{ color: 'var(--erp-ink)' }}>{title}</h1>
      {subtitle && <p className="text-sm mt-1" style={{ color: 'var(--erp-muted)' }}>{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const EmptyState = ({ icon: Icon = Inbox, title, text, action }) => (
  <div className="py-14 text-center">
    <div className="mx-auto h-14 w-14 rounded-2xl grid place-items-center mb-4" style={{ background: 'var(--erp-blue-soft)', color: 'var(--erp-blue)' }}><Icon size={24} /></div>
    <h4 className="font-bold" style={{ color: 'var(--erp-ink)' }}>{title}</h4>
    {text && <p className="text-sm mt-1 max-w-sm mx-auto" style={{ color: 'var(--erp-muted)' }}>{text}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const Table = ({ columns, rows, rowKey = 'id', onRowClick, empty, loading, testid = 'table' }) => (
  <div className="overflow-x-auto erp-scroll">
    <table className="erp-table" data-testid={testid}>
      <thead><tr>{columns.map((c) => <th key={c.key} className={c.className || ''} style={{ textAlign: c.align || 'left' }}>{c.label}</th>)}</tr></thead>
      <tbody>
        {loading && [0, 1, 2, 3].map((i) => (
          <tr key={`sk-${i}`}>{columns.map((c) => <td key={c.key}><div className="erp-skeleton h-4 w-3/4" /></td>)}</tr>
        ))}
        {!loading && rows.length === 0 && (
          <tr><td colSpan={columns.length}>{empty || <EmptyState title="Nothing here yet" />}</td></tr>
        )}
        {!loading && rows.map((r) => (
          <tr key={r[rowKey]} onClick={onRowClick ? () => onRowClick(r) : undefined} className={onRowClick ? 'erp-row-click' : ''} data-testid={`${testid}-row-${r[rowKey]}`}>
            {columns.map((c) => <td key={c.key} className={c.className || ''} style={{ textAlign: c.align || 'left' }}>{c.render ? c.render(r) : (r[c.key] ?? '—')}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const Modal = ({ open, onClose, title, subtitle, children, footer, width = 'max-w-xl', testid = 'modal' }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto p-4 sm:p-8 erp-root" style={{ background: 'transparent' }}>
          <motion.div className="fixed inset-0" style={{ background: 'rgba(11,26,74,.55)', backdropFilter: 'blur(4px)' }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
          <motion.div role="dialog" aria-modal="true" data-testid={testid}
            className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl mt-6`}
            initial={{ opacity: 0, y: 18, scale: .98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: .98 }}
            transition={{ duration: .22, ease: [.2, .7, .2, 1] }}>
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b" style={{ borderColor: 'var(--erp-line)' }}>
              <div>
                <h2 className="text-lg font-extrabold" style={{ color: 'var(--erp-ink)' }}>{title}</h2>
                {subtitle && <p className="text-sm mt-0.5" style={{ color: 'var(--erp-muted)' }}>{subtitle}</p>}
              </div>
              <button type="button" onClick={onClose} className="h-9 w-9 rounded-lg grid place-items-center hover:bg-gray-100 transition-colors" data-testid={`${testid}-close`}><X size={18} /></button>
            </div>
            <div className="px-6 py-5">{children}</div>
            {footer && <div className="flex items-center justify-end gap-2 px-6 py-4 rounded-b-2xl border-t" style={{ borderColor: 'var(--erp-line)', background: '#fafbfe' }}>{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export const FormModal = ({ open, onClose, onSubmit, busy, submitLabel = 'Save', children, ...rest }) => (
  <Modal open={open} onClose={onClose} {...rest}
    footer={<>
      <Button variant="ghost" onClick={onClose} data-testid={`${rest.testid || 'modal'}-cancel`}>Cancel</Button>
      <Button form={`${rest.testid || 'modal'}-form`} type="submit" disabled={busy} data-testid={`${rest.testid || 'modal'}-submit`}>{busy ? 'Saving…' : submitLabel}</Button>
    </>}>
    <form id={`${rest.testid || 'modal'}-form`} onSubmit={(e) => { e.preventDefault(); onSubmit(); }} className="space-y-4">{children}</form>
  </Modal>
);

const useEscape = (open, onClose) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
};

export const Drawer = ({ open, onClose, title, subtitle, children, testid = 'drawer' }) => {
  useEscape(open, onClose);
  return createPortal(
  <AnimatePresence>
    {open && (
      <div className="fixed inset-0 z-[100] erp-root" style={{ background: 'transparent' }}>
        <motion.div className="absolute inset-0" style={{ background: 'rgba(11,26,74,.45)' }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} />
        <motion.aside data-testid={testid} className="absolute right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl flex flex-col"
          initial={{ x: 40, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 40, opacity: 0 }} transition={{ duration: .25, ease: [.2, .7, .2, 1] }}>
          <div className="flex items-start justify-between px-6 py-5 border-b" style={{ borderColor: 'var(--erp-line)' }}>
            <div><h2 className="text-lg font-extrabold">{title}</h2>{subtitle && <p className="text-sm" style={{ color: 'var(--erp-muted)' }}>{subtitle}</p>}</div>
            <button type="button" onClick={onClose} className="h-9 w-9 rounded-lg grid place-items-center hover:bg-gray-100" data-testid={`${testid}-close`}><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto erp-scroll px-6 py-5">{children}</div>
        </motion.aside>
      </div>
    )}
  </AnimatePresence>,
  document.body,
  );
};
